import { Component, Input, OnChanges, OnDestroy, SimpleChanges, inject } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FormValidationService } from '../validation/form-validation.service';

enum EmailParticle {
  LocalPart = 'localPart',
  Domain = 'domain',
}

@Component({
  selector: 'app-email-domain-input',
  templateUrl: './email-domain-input.component.html',
  styleUrls: ['./email-domain-input.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: EmailDomainInputComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: EmailDomainInputComponent,
    },
  ],
})
export class EmailDomainInputComponent implements ControlValueAccessor, Validator, OnDestroy, OnChanges {
  private validation = inject(FormValidationService);

  public readonly EmailParticle = EmailParticle;

  @Input()
  public name: string;

  @Input()
  public domains: string[] = [];

  public onTouched: Function = () => {};
  private onChangeSubs: Subscription[] = [];

  public readonly emailGroup = new FormGroup({
    [EmailParticle.LocalPart]: new FormControl(''),
    [EmailParticle.Domain]: new FormControl(''),
  });
  public get localPart() { return this.emailGroup.controls[EmailParticle.LocalPart]; }
  public get domain() { return this.emailGroup.controls[EmailParticle.Domain]; }

  get value(): string {
    const localPart = this.localPart.value?.trim();
    const domain = this.domain.value?.trim();
    if (localPart && domain) {
      return `${localPart}@${domain}`;
    } else {
      return null;
    }
  }

  set value(value: string) {
    if (value.includes("@")) {
      const parts = value.split("@", 2);
      this.localPart.setValue(parts[0]);
      this.domain.setValue(parts[1]);
    } else {
      this.localPart.setValue(value);
      this.domain.setValue(null);
    }
  }

  public writeValue(value: any): void {
    if (typeof value === 'string' || value instanceof String) {
      this.value = value as string;
    } else if (value === null) {
      this.localPart.reset();
      this.domain.markAsPristine();
    } else {
      throw Error('invalid value type');
    }
  }

  public registerOnChange(onChange: any): void {
    const sub = this.emailGroup.valueChanges.subscribe(() => onChange(this.value));
    this.onChangeSubs.push(sub);
  }

  public registerOnTouched(onTouched: Function): void {
    this.onTouched = onTouched;
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.emailGroup.disable();
    } else {
      this.emailGroup.enable();
    }
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    if (!this.value) {
      return null;
    }
    return !this.validation.emailValidator.isValid(control.value) ? { pattern: true } : null;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const domains = changes.domains?.currentValue;
    if (Array.isArray(domains)) {
      if (domains.length > 0) {
        setTimeout(() => this.domain.setValue(domains[0]), 0);
      } else {
        this.domain.setValue(null);
      }
    }
  }

  public ngOnDestroy(): void {
    for (let sub of this.onChangeSubs) {
      sub.unsubscribe();
    }
  }
}
